import {
  ButtonHTMLAttributes,
  forwardRef,
  ReactNode,
  useCallback,
  useState,
} from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'confirm';
export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  loading?: boolean;
  children?: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

/**
 * Themeable button.
 *
 * Visual style comes entirely from `.ds-btn` + the `data-ds-variant` hook, so
 * a theme can restyle (or completely re-model, as Tactile and Liquid Mercury
 * do) without this file knowing.
 *
 * `confirm` is a first-class variant rather than a colour of `primary`. It
 * marks the commit action of a flow, which lets a theme give it a distinct
 * identity — Saga Gold renders it in the classic golden livery.
 *
 * ## Why the manual pressed state
 * Themes with press physics need an "is being pressed" signal. `:active` fires
 * for mouse, but keyboard activation is inconsistent: Enter on a <button>
 * gives at most a single frame of `:active` in most engines. Without this,
 * keyboard users would get no press feedback at all — so we mirror the state
 * onto `data-ds-pressed` from key events. Themes key off both.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'secondary',
      size = 'md',
      startIcon,
      endIcon,
      fullWidth,
      loading = false,
      disabled,
      children,
      className = '',
      onKeyDown,
      onKeyUp,
      onBlur,
      ...rest
    },
    ref,
  ) {
    const [pressed, setPressed] = useState(false);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ') setPressed(true);
        onKeyDown?.(e);
      },
      [onKeyDown],
    );

    const handleKeyUp = useCallback(
      (e: React.KeyboardEvent<HTMLButtonElement>) => {
        setPressed(false);
        onKeyUp?.(e);
      },
      [onKeyUp],
    );

    // Losing focus mid-press (e.g. the click opened a dialog) would otherwise
    // leave the button stuck in its pressed state.
    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLButtonElement>) => {
        setPressed(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    return (
      <button
        ref={ref}
        type="button"
        className={`ds-btn ${className}`}
        data-ds-variant={variant}
        data-ds-size={size}
        data-ds-full={fullWidth ? 'true' : undefined}
        data-ds-pressed={pressed ? 'true' : undefined}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        {...rest}
      >
        {loading ? (
          <span className="ds-btn-spinner" aria-hidden="true" />
        ) : (
          startIcon
        )}
        {children}
        {endIcon}
      </button>
    );
  },
);

type IconButtonProps = {
  /** Required: an icon-only control has no text to name it. */
  label: string;
  size?: 'sm' | 'md';
  tone?: 'default' | 'onDark';
  children: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'aria-label'>;

/**
 * Icon-only button. `label` is mandatory and becomes `aria-label` — the
 * production IconButton leaves this to call sites, and many forget.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { label, size = 'md', tone = 'default', children, className = '', ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={`ds-icon-btn ${className}`}
        data-ds-size={size}
        data-ds-tone={tone === 'onDark' ? 'onDark' : undefined}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
